import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserAuth } from '../../context/UserAuthContext';
import { useToast } from '../../context/ToastContext';
import { Mail, Lock, User, ArrowRight, Phone } from 'lucide-react';
import { TextField, Button, Typography, Paper, Box, InputAdornment, CircularProgress } from '@mui/material';
import './AuthPage.css';

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const { login, register, currentUser, isAuthenticated } = useUserAuth();
  const { showSuccessToast, showErrorToast } = useToast();
  const navigate = useNavigate();

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const validateForm = () => {
    if (!email || !password) {
      showErrorToast('Please fill in all required fields');
      return false;
    }
    
    if (!isLogin && (!firstName || !lastName)) {
      showErrorToast('Please enter your first and last name');
      return false;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showErrorToast('Please enter a valid email address');
      return false;
    }
    
    // Basic password validation
    if (password.length < 6) {
      showErrorToast('Password must be at least 6 characters long');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      if (isLogin) {
        await login(email, password);
        // Navigation happens automatically in the useEffect
      } else {
        const userData = {
          email,
          password,
          firstName,
          lastName,
          phone: phone || null
        };
        
        await register(userData);
        // Navigation happens automatically in the useEffect
      }
    } catch (error) {
      // Error handling is done in the context
      console.error('Auth error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleAuthMode = () => {
    setIsLogin(!isLogin);
    // Clear form when switching modes
    setEmail('');
    setPassword('');
    setFirstName('');
    setLastName('');
    setPhone('');
  };

  return (
    <Box className="auth-page" display="flex" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="var(--deep-space)" mt={{ xs: 2, sm: 4 }}>
      <Paper className="auth-container" elevation={3} sx={{ padding: '2rem', borderRadius: '12px', maxWidth: '500px', width: '100%', bgcolor: 'var(--form-bg)' }}>
        <Typography variant="h4" component="h1" gutterBottom color="var(--neon-blue)">{isLogin ? 'Login' : 'Sign Up'}</Typography>
        <Typography variant="body1" gutterBottom color="var(--quantum-silver)">{isLogin ? 'Welcome back!' : 'Create your account'}</Typography>
        <form className="auth-form" onSubmit={handleSubmit}>
          {!isLogin && (
            <>
              <TextField
                label="First Name"
                variant="outlined"
                fullWidth
                margin="normal"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={isLoading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <User size={18} className="form-icon" />
                    </InputAdornment>
                  )
                }}
                sx={{ input: { color: 'var(--quantum-silver)' }, label: { color: 'var(--quantum-silver)' } }}
              />
              <TextField
                label="Last Name"
                variant="outlined"
                fullWidth
                margin="normal"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={isLoading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <User size={18} className="form-icon" />
                    </InputAdornment>
                  )
                }}
                sx={{ input: { color: 'var(--quantum-silver)' }, label: { color: 'var(--quantum-silver)' } }}
              />
              <TextField
                label="Phone (Optional)"
                variant="outlined"
                fullWidth
                margin="normal"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={isLoading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone size={18} className="form-icon" />
                    </InputAdornment>
                  )
                }}
                sx={{ input: { color: 'var(--quantum-silver)' }, label: { color: 'var(--quantum-silver)' } }}
              />
            </>
          )}
          <TextField
            label="Email"
            type="email"
            variant="outlined"
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Mail size={18} className="form-icon" />
                </InputAdornment>
              )
            }}
            sx={{ input: { color: 'var(--quantum-silver)' }, label: { color: 'var(--quantum-silver)' } }}
          />
          <TextField
            label="Password"
            type="password"
            variant="outlined"
            fullWidth
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock size={18} className="form-icon" />
                </InputAdornment>
              )
            }}
            sx={{ input: { color: 'var(--quantum-silver)' }, label: { color: 'var(--quantum-silver)' } }}
          />
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            fullWidth
            disabled={isLoading}
            sx={{ 
              marginTop: '1rem', 
              bgcolor: 'var(--neon-blue)', 
              color: 'var(--deep-space)',
              '&:hover': {
                bgcolor: 'var(--stellar-purple)'
              }
            }}
          >
            {isLoading ? (
              <CircularProgress size={24} sx={{ color: 'var(--deep-space)' }} />
            ) : (
              isLogin ? 'Login' : 'Sign Up'
            )}
          </Button>
        </form>
        <Box className="auth-switch" mt={2}>
          <Typography variant="body2" color="var(--quantum-silver)">
            {isLogin ? "Don't have an account?" : "Already have an account?"}
            <Button 
              type="button" 
              onClick={toggleAuthMode}
              className="switch-button"
              disabled={isLoading}
              sx={{ marginLeft: '0.5rem', color: 'var(--neon-blue)' }}
            >
              {isLogin ? 'Sign Up' : 'Login'}
            </Button>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default AuthPage; 